<template>
  <div class="page-container">
    <div class="header-row">
      <h2 class="page-title">{{ $t('household.nomenclatures.title') }}</h2>
      <button class="btn-primary" @click="openDialog()">
        <LucideIcon :size="16" class="mr-2">
          <path d="M5 12h14"/><path d="M12 5v14"/>
        </LucideIcon>
        {{ $t('household.nomenclatures.addBtn') }}
      </button>
    </div>

    <!-- Фильтры -->
    <div class="filter-card">
      <div class="search-wrapper">
        <LucideIcon :size="18" class="search-icon">
          <circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>
        </LucideIcon>
        <input
          v-model="searchTerm"
          type="text"
          :placeholder="$t('household.nomenclatures.searchPlaceholder')"
          class="search-input"
        />
      </div>
      <select v-model="filterType" class="type-select">
        <option value="all">{{ $t('household.shared.allTypes') }}</option>
        <option v-for="t in nomenclatureTypes" :key="t.id" :value="t.id">{{ t.name }}</option>
      </select>
    </div>

    <!-- Таблица -->
    <div class="table-container">
      <table class="data-table">
        <thead>
          <tr>
            <th>{{ $t('household.nomenclatures.colName') }}</th>
            <th>{{ $t('household.nomenclatures.colCode') }}</th>
            <th>{{ $t('household.nomenclatures.colType') }}</th>
            <th>{{ $t('household.nomenclatures.colAssetType') }}</th>
            <!-- <th>Счет учета</th> TODO: Раскомментировать когда backend добавит uchet_number -->
            <th>{{ $t('household.nomenclatures.colUnit') }}</th>
            <th>{{ $t('household.nomenclatures.colPrice') }}</th>
            <th class="text-right">{{ $t('common.actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in filteredNomenclatures" :key="item.id">
            <td>
              <div class="item-name">{{ item.name }}</div>
              <div v-if="item.description" class="item-desc">{{ item.description }}</div>
            </td>
            <td>{{ item.code || '—' }}</td>
            <td>
              <span :class="['badge', getTypeClass(item.type)]">
                {{ typeLabels[item.type] || item.type }}
              </span>
            </td>
            <td>
              <span :class="['badge', item.asset_type === 'fixed_asset' ? 'fixed-asset' : 'inventory']">{{ item.asset_type === 'fixed_asset' ? $t('household.assetTypes.fixedAssetShort') : $t('household.assetTypes.inventory') }}</span>
            </td>
            <!-- <td>{{ getAccountLabel(item.uchet_number) }}</td> TODO: Раскомментировать когда backend добавит uchet_number -->
            <td>{{ item.unit }}</td>
            <td>{{ formatCurrency(item.price) }}</td>
            <td class="text-right">
              <button class="action-btn edit" @click="openDialog(item)">
                <LucideIcon :size="16">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                </LucideIcon>
              </button>
              <button class="action-btn delete" @click="handleDelete(item.id)">
                <LucideIcon :size="16">
                  <path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/>
                </LucideIcon>
              </button>
            </td>
          </tr>
          <tr v-if="filteredNomenclatures.length === 0">
            <td colspan="8" class="empty-row">{{ $t('household.nomenclatures.notFound') }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Диалог -->
    <div v-if="isDialogOpen" class="modal-overlay" @click.self="closeDialog">
      <div class="modal-content">
        <h3 class="modal-title">
          {{ editingItem ? $t('household.nomenclatures.dialogEditTitle') : $t('household.nomenclatures.dialogAddTitle') }}
        </h3>
        <form @submit.prevent="handleSubmit" class="modal-form">
          <div class="form-group">
            <label>{{ $t('household.nomenclatures.colName') }}</label>
            <input v-model="formData.name" type="text" required :placeholder="$t('household.nomenclatures.namePlaceholder')" />
          </div>

          <div class="form-group">
            <label>{{ $t('household.nomenclatures.colCode') }}</label>
            <input v-model="formData.code" type="text" :placeholder="$t('household.nomenclatures.codePlaceholder')" />
          </div>

          <div class="form-row">
            <div class="form-group flex-1">
              <label>{{ $t('household.nomenclatures.colType') }}</label>
              <select v-model="formData.type">
                <option v-for="t in nomenclatureTypes" :key="t.id" :value="t.id">{{ t.name }}</option>
              </select>
            </div>
            <div class="form-group flex-1">
              <label>{{ $t('household.nomenclatures.colAssetType') }}</label>
              <select v-model="formData.asset_type">
                <option value="inventory">{{ $t('household.assetTypes.inventory') }}</option>
                <option value="fixed_asset">{{ $t('household.assetTypes.fixedAsset') }}</option>
              </select>
            </div>
          </div>

          <!-- TODO: Раскомментировать когда backend добавит uchet_number
          <div class="form-group">
            <label>Счет учета</label>
            <select v-model="formData.uchet_number">
              <option value="">Не выбран</option>
              <option v-for="acc in accountCodes" :key="acc.id" :value="acc.id">{{ acc.code }} {{ acc.name }}</option>
            </select>
          </div>
          -->

          <div class="form-row">
            <div class="form-group flex-1">
              <label>{{ $t('household.nomenclatures.colUnit') }}</label>
              <input v-model="formData.unit" type="text" required :placeholder="$t('household.nomenclatures.unitPlaceholder')" />
            </div>
            <div class="form-group flex-1">
              <label>{{ $t('household.nomenclatures.colPrice') }}</label>
              <input v-model.number="formData.price" type="number" step="0.01" required />
            </div>
            <div class="form-group flex-1">
              <label>{{ $t('household.nomenclatures.shelfLifeLabel') }}</label>
              <input v-model.number="formData.shelf_life_days" type="number" min="0" required />
            </div>
          </div>

          <div class="form-group">
            <label>{{ $t('household.nomenclatures.descriptionLabel') }}</label>
            <textarea v-model="formData.description" rows="2"></textarea>
          </div>

          <div class="modal-actions">
            <button type="button" class="btn-secondary" @click="closeDialog">{{ $t('common.cancel') }}</button>
            <button type="submit" class="btn-primary">{{ editingItem ? $t('common.save') : $t('common.add') }}</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import LucideIcon from '@/components/common/LucideIcon.vue';
import { 
  getNomenclatures, 
  createNomenclature, 
  updateNomenclature, 
  deleteNomenclature,
  getNomenclatureTypes,
  createNomenclatureType,
  getAccountCodes
} from '@/api/household_nomenclatures';

export default {
  name: 'HouseholdNomenclaturesView',
  components: { LucideIcon },
  data() {
    return {
      nomenclatures: [],
      searchTerm: '',
      filterType: 'all',
      isDialogOpen: false,
      editingItem: null,
      formData: {
        name: '',
        code: '',
        type: '',
        asset_type: 'inventory',
        uchet_number: '',
        unit: 'шт',
        price: 0,
        shelf_life_days: 0,
        description: '',
      },
      typeLabels: {},
      nomenclatureTypes: [],
      accountCodes: []
    };
  },
  computed: {
    filteredNomenclatures() {
      return this.nomenclatures.filter((item) => {
        const matchesSearch = (item.name || '').toLowerCase().includes(this.searchTerm.toLowerCase());
        const typeId = item.nomenclature_type || item.type;
        const matchesType = this.filterType === 'all' || String(typeId) === String(this.filterType);
        return matchesSearch && matchesType;
      });
    }
  },
  async created() {
    await Promise.all([
      this.loadTypes(),
      // this.loadAccountCodes() // TODO: Раскомментировать когда backend добавит uchet_number
    ]);
    await this.loadNomenclatures();
  },
  methods: {
    async loadTypes() {
      try {
        const res = await getNomenclatureTypes({ page_size: 100 });
        const backendTypes = Array.isArray(res.data) ? res.data : (res.data?.results || []);
        
        const standardNames = ['ГСМ', 'Собаки', 'Товары', 'Оружие'];
        const types = [];

        // Ensure standard types are always present
        standardNames.forEach(name => {
          const found = backendTypes.find(t => t.name === name);
          if (found) {
            types.push(found);
          } else {
            // Placeholder with name as ID if not on backend
            types.push({ id: name, name: name });
          }
        });

        // Add other types from backend
        backendTypes.forEach(t => {
          if (!standardNames.includes(t.name)) {
            types.push(t);
          }
        });

        this.nomenclatureTypes = types;
        const labels = {};
        this.nomenclatureTypes.forEach(t => {
          labels[t.id] = t.name;
        });
        this.typeLabels = labels;
      } catch (err) {
        console.error('Failed to load nomenclature types:', err);
      }
    },
    async loadAccountCodes() {
      /* TODO: Раскомментировать когда backend добавит uchet_number
      try {
        const res = await getAccountCodes();
        this.accountCodes = Array.isArray(res.data) ? res.data : (res.data?.results || []);
      } catch (err) {
        console.error('Failed to load account codes:', err);
      }
      */
    },
    getAccountLabel(id) {
      if (!id) return '—';
      const acc = this.accountCodes.find(a => a.id === id);
      return acc ? `${acc.code} ${acc.name}` : '—';
    },
    async loadNomenclatures() {
      const res = await getNomenclatures();
      const data = Array.isArray(res.data) ? res.data : (res.data?.results || []);
      this.nomenclatures = data.map(item => ({
        ...item,
        type: item.nomenclature_type || item.type,
      }));
    },
    getTypeClass(typeId) {
      const label = this.typeLabels[typeId] || '';
      const map = {
        'ГСМ': 'fuel',
        'Собаки': 'dogs',
        'Товары': 'goods',
        'Оружие': 'weapons'
      };
      return map[label] || 'goods';
    },
    formatCurrency(val) {
      return new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'KZT' }).format(val);
    },
    openDialog(item = null) {
      if (item) {
        this.editingItem = item;
        this.formData = { ...item };
      } else {
        this.editingItem = null;
        this.formData = {
          name: '',
          code: '',
          type: this.nomenclatureTypes.length > 0 ? this.nomenclatureTypes[0].id : '',
          asset_type: 'inventory',
          uchet_number: '',
          unit: 'шт',
          price: 0,
          shelf_life_days: 0,
          description: '',
        };
      }
      this.isDialogOpen = true;
    },
    closeDialog() {
      this.isDialogOpen = false;
    },
    async handleSubmit() {
      try {
        let typeId = this.formData.type;
        
        // If the selected type is a standard name but not on backend, 
        // we try to create it automatically or find its real ID
        const isStandardPlaceholder = ['ГСМ', 'Собаки', 'Товары', 'Оружие'].includes(typeId);
        if (isStandardPlaceholder) {
          try {
            const createRes = await createNomenclatureType({ name: typeId });
            typeId = createRes.data.id;
            await this.loadTypes(); 
          } catch (e) {
            // If creation fails, maybe it already exists but was not loaded
            const findRes = await getNomenclatureTypes({ search: typeId });
            const list = Array.isArray(findRes.data) ? findRes.data : (findRes.data?.results || []);
            const found = list.find(t => t.name === typeId);
            if (found) {
              typeId = found.id;
            } else {
              console.warn(`Could not sync type "${typeId}", proceeding with placeholder ID`);
            }
          }
        }

        const payload = {
          name: this.formData.name,
          code: this.formData.code || '',
          nomenclature_type: typeId,
          asset_type: this.formData.asset_type || 'inventory',
          // uchet_number: this.formData.uchet_number || null, // TODO: Раскомментировать когда backend добавит uchet_number
          shelf_life_days: Number(this.formData.shelf_life_days) || 0,
          unit: this.formData.unit,
          price: Number(this.formData.price) || 0,
          description: this.formData.description || '',
        };

        if (this.editingItem) {
          await updateNomenclature(this.editingItem.id, payload);
        } else {
          await createNomenclature(payload);
        }
        await this.loadNomenclatures();
        this.closeDialog();
      } catch (err) {
        console.error('Submit error:', err);
        let msg = this.$t('household.nomenclatures.saveError');
        if (err.response?.data) {
          msg += ': ' + JSON.stringify(err.response.data);
        } else if (err.message) {
          msg += ': ' + err.message;
        }
        alert(msg);
      }
    },
    async handleDelete(id) {
      if (confirm(this.$t('household.nomenclatures.confirmDelete'))) {
        await deleteNomenclature(id);
        await this.loadNomenclatures();
      }
    }
  }
};
</script>

<style scoped>
.page-container { padding: 2rem; background: #f3f3f5; min-height: 100%; }
.header-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 2rem; }
.page-title { color: #000000; font-size: 1.75rem; font-weight: 700; margin: 0; }

.filter-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 1rem;
  padding: 1.25rem;
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
  align-items: center;
}

.search-wrapper { position: relative; flex: 1; max-width: 400px; }
.search-icon { position: absolute; left: 1rem; top: 50%; transform: translateY(-50%); color: #94a3b8; }
.search-input {
  width: 100%;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 2rem;
  padding: 0.75rem 1rem 0.75rem 3rem;
  color: #030229;
  font-size: 0.95rem;
  outline: none;
  transition: all 0.2s;
}
.search-input:focus { border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1); }

.type-select {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  padding: 0.75rem 1rem;
  color: #030229;
  outline: none;
  min-width: 150px;
}

.table-container {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 1rem;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
}

.data-table { width: 100%; border-collapse: collapse; }
.data-table th {
  background: #fafafa;
  text-align: left;
  padding: 1rem 1.5rem;
  color: #717182;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid #f1f1f4;
}
.data-table td { padding: 1.25rem 1.5rem; border-top: 1px solid #f1f1f4; color: #030229; vertical-align: middle; }

.item-name { font-weight: 600; color: #030229; font-size: 1rem; }
.item-desc { font-size: 0.8rem; color: #717182; margin-top: 0.2rem; }

.badge {
  display: inline-flex;
  padding: 0.35rem 0.75rem;
  border-radius: 2rem;
  font-size: 0.7rem;
  font-weight: 600;
}
.badge.fuel { background: #fef9c3; color: #a16207; }
.badge.dogs { background: #dcfce7; color: #15803d; }
.badge.goods { background: #dbeafe; color: #1d4ed8; }
.badge.weapons { background: #fee2e2; color: #b91c1c; }

.text-right { text-align: right; }
.action-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0.4rem;
  margin-left: 0.5rem;
  border-radius: 0.5rem;
  transition: all 0.2s;
  color: #3b82f6;
}
.action-btn.delete { color: #ef4444; }
.action-btn:hover { background: #f1f5f9; }

.empty-row { text-align: center; padding: 4rem; color: #717182; font-size: 1rem; }

.btn-primary {
  background: #2563eb;
  color: #fff;
  border: none;
  border-radius: 0.75rem;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s;
}
.btn-primary:hover { background: #1d4ed8; transform: translateY(-1px); box-shadow: 0 4px 6px -1px rgba(37, 99, 235, 0.2); }

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.modal-content {
  background: #ffffff;
  border-radius: 1.25rem;
  width: 100%;
  max-width: 550px;
  padding: 2rem;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
}
.modal-title { color: #030229; font-size: 1.5rem; font-weight: 700; margin-bottom: 2rem; }

.form-group { margin-bottom: 1.25rem; }
.form-group label { display: block; color: #4b5563; font-size: 0.875rem; font-weight: 500; margin-bottom: 0.5rem; }
.form-group input, .form-group select, .form-group textarea {
  width: 100%;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  padding: 0.75rem 1rem;
  color: #111827;
  outline: none;
  transition: all 0.2s;
}
.form-group input:focus { border-color: #3b82f6; background: #ffffff; box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1); }

.form-row { display: flex; gap: 1rem; }
.flex-1 { flex: 1; }

.modal-actions { display: flex; gap: 1rem; margin-top: 2.5rem; }
.btn-secondary {
  flex: 1;
  background: #f3f4f6;
  color: #4b5563;
  border: none;
  border-radius: 0.75rem;
  padding: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}
.btn-secondary:hover { background: #e5e7eb; }

.btn-submit {
  flex: 1;
  background: #2563eb;
  color: white;
  border: none;
  border-radius: 0.75rem;
  padding: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}
.btn-submit:hover { background: #1d4ed8; }
</style>
